import { Link } from "react-router-dom";
import { Plus, CalendarClock, FileText, Hammer, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/api";
import { fmtDate, fmtDateTime, gbp, ACTIVE_JOB } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorBox, EmptyState, Section, StatusBadge } from "@/components/Common";
import { ActivityList } from "@/components/Activity";

const Row = ({ to, title, sub, status, customer, testId }) => (
  <Link to={to} data-testid={testId} className="flex items-center justify-between gap-3 rounded-md border border-border p-3 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring">
    <div className="min-w-0"><p className="truncate text-sm font-medium">{title}</p><p className="truncate text-xs text-muted-foreground">{sub}</p></div>
    <StatusBadge status={status} customer={customer} />
  </Link>
);

const List = ({ items, empty, render }) => (items.length ? <div className="space-y-2">{items.map(render)}</div> : <p className="text-sm text-muted-foreground">{empty}</p>);

export default function CustomerDashboard() {
  const { user } = useAuth();
  const { data, loading, error } = useFetch("/customer/dashboard");
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = data.appointments.filter((a) => a.status === "Confirmed" && a.date >= today);
  const pending = data.appointments.filter((a) => ["Requested", "Rescheduling Requested"].includes(a.status));
  const quotes = data.quotations.filter((q) => q.status === "Sent");
  const active = data.jobs.filter((j) => ACTIVE_JOB.includes(j.status));
  const done = data.jobs.filter((j) => j.status === "Completed");
  const apptRow = (a) => <Row key={a.id} to="/portal/appointments" testId={`dash-appt-${a.id}`} title={`${a.type} · ${fmtDate(a.date)} ${a.start}`} sub={a.request_reference} status={a.status} />;
  return (
    <div>
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle="Track your landscaping requests, appointments, quotations and jobs."
        actions={<Button asChild data-testid="dash-new-request-btn"><Link to="/portal/new"><Plus className="mr-1 h-4 w-4" />New job request</Link></Button>} />
      {!data.requests.length ? (
        <EmptyState testId="dash-empty" title="No job requests yet" text="Tell us about the work you need and upload a few photos. It only takes a few minutes."
          action={<Button asChild><Link to="/portal/new">Start a job request</Link></Button>} />
      ) : (
        <div className="grid gap-5 lg:grid-cols-3">
          <Section title={<span className="flex items-center gap-2"><CalendarClock className="h-4 w-4" />Upcoming appointments</span>} testId="dash-upcoming">
            <List items={upcoming} empty="No confirmed appointments." render={apptRow} />
          </Section>
          <Section title="Pending appointment requests" testId="dash-pending">
            <p className="mb-2 text-xs text-muted-foreground">Awaiting confirmation from DJ Landscapes - not yet confirmed.</p>
            <List items={pending} empty="No pending requests." render={apptRow} />
          </Section>
          <Section title={<span className="flex items-center gap-2"><FileText className="h-4 w-4" />Outstanding quotations</span>} testId="dash-quotes">
            <List items={quotes} empty="No quotations awaiting your response." render={(q) => <Row key={q.id} to={`/portal/quotations/${q.id}`} testId={`dash-quote-${q.id}`} title={`${q.reference} · ${gbp(q.total)}`} sub={`Valid until ${fmtDate(q.expiry_date)}`} status={q.status} />} />
          </Section>
          <Section title={<span className="flex items-center gap-2"><Hammer className="h-4 w-4" />Active jobs</span>} testId="dash-active-jobs">
            <List items={active} empty="No active jobs." render={(j) => <Row key={j.id} to={`/portal/jobs/${j.id}`} testId={`dash-job-${j.id}`} title={j.reference} sub={j.property_address} status={j.status} />} />
          </Section>
          <Section title={<span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Completed jobs</span>} testId="dash-completed-jobs">
            <List items={done} empty="No completed jobs yet." render={(j) => <Row key={j.id} to={`/portal/jobs/${j.id}`} title={j.reference} sub={j.property_address} status={j.status} />} />
          </Section>
          <Section title="Your job requests" testId="dash-requests">
            <List items={data.requests.slice(0, 6)} empty="" render={(r) => <Row key={r.id} to={`/portal/requests/${r.id}`} testId={`dash-request-${r.reference}`} title={r.reference} sub={r.services.map((s) => s.name).join(", ")} status={r.status} customer />} />
          </Section>
          <Section title="Recent activity" className="lg:col-span-3" testId="dash-activity">
            <ActivityList items={data.activity} />
          </Section>
        </div>
      )}
      <p className="mt-6 text-xs text-muted-foreground">Last updated {fmtDateTime(new Date().toISOString())}</p>
    </div>
  );
}
